import { TaskValue } from 'lib/actions/task'
import { UserValue } from 'lib/actions/user'
import { UsersState } from 'lib/reducers/users'
import { Status } from 'lib/rpc'
import { PolicyInstance, PolicyStep, PolicyStepInstance, Provision } from 'pbts/c1/api/policy/v1/policy'
import { Approval } from 'pbts/c1/api/policy/v1/policy_steps_approval'
import { Wait, WaitInstance } from 'pbts/c1/api/policy/v1/policy_steps_wait'
import { IntlShape } from 'react-intl'

export const getApprovalTypei18n = ({ approval, intl }: { approval?: Approval; intl: IntlShape }): string => {
    switch (approval?.typ?.oneofKind) {
        case 'users':
            return intl.formatMessage({
                id: 'component_task_details_policy_workflow_policy_step_approval_type_users',
                defaultMessage: 'Specific users',
            })
        case 'agent':
            return intl.formatMessage({
                id: 'component_task_details_policy_workflow_policy_step_approval_type_agent',
                defaultMessage: 'Agent',
            })
        case 'manager':
            return intl.formatMessage({
                id: 'component_task_details_policy_workflow_policy_step_approval_type_manager',
                defaultMessage: 'Manager',
            })
        case 'appOwners':
            return intl.formatMessage({
                id: 'component_task_details_policy_workflow_policy_step_approval_type_app_owners',
                defaultMessage: 'App owners',
            })
        case 'entitlementOwners':
            return intl.formatMessage({
                id: 'component_task_details_policy_workflow_policy_step_approval_type_entitlement_owners',
                defaultMessage: 'Entitlement owners',
            })
        case 'group':
            return intl.formatMessage({
                id: 'component_task_details_policy_workflow_policy_step_approval_type_group',
                defaultMessage: 'Group',
            })
        case 'self':
            return intl.formatMessage({
                id: 'component_task_details_policy_workflow_policy_step_approval_type_self',
                defaultMessage: 'Self',
            })
        case 'expression':
            return intl.formatMessage({
                id: 'component_task_details_policy_workflow_policy_step_approval_type_expression',
                defaultMessage: 'Custom',
            })
        case 'webhook':
            return intl.formatMessage({
                id: 'component_task_details_policy_workflow_policy_step_approval_type_webhook',
                defaultMessage: 'Webhook',
            })
        case 'resourceOwners':
            return intl.formatMessage({
                id: 'component_task_details_policy_workflow_policy_step_approval_type_resource_owners',
                defaultMessage: 'Resource owners',
            })
        default:
            return intl.formatMessage({
                id: 'component_task_details_policy_workflow_policy_step_approval_type_not_found',
                defaultMessage: 'Approval type not found',
            })
    }
}

export const getProvisionTypei18n = (
    { provision, task, intl }: { provision?: Provision; task: TaskValue; intl: IntlShape },
): string => {
    switch (provision?.provisionPolicy?.typ?.oneofKind) {
        case 'connector':
        case 'unconfigured':
            switch (task.type?.taskType.oneofKind) {
                case 'grant':
                    return intl.formatMessage({
                        id: 'component_task_details_policy_workflow_policy_step_provision_type_connector_grant',
                        defaultMessage: 'Connector action: provision entitlement',
                    })
                case 'revoke':
                    return intl.formatMessage({
                        id: 'component_task_details_policy_workflow_policy_step_provision_type_connector_revoke',
                        defaultMessage: 'Connector action: revoke entitlement',
                    })
                default:
                    return intl.formatMessage({
                        id: 'component_task_details_policy_workflow_policy_step_provision_type_connector_default',
                        defaultMessage: 'Connector action',
                    })
            }
        case 'manual':
            return intl.formatMessage({
                id: 'component_task_details_policy_workflow_policy_step_provision_type_manual',
                defaultMessage: 'Manual access change',
            })
        case 'delegated':
            return intl.formatMessage({
                id: 'component_task_details_policy_workflow_policy_step_provision_type_delegated',
                defaultMessage: 'Delegated action',
            })
        case 'webhook':
            return intl.formatMessage({
                id: 'component_task_details_policy_workflow_policy_step_provision_type_webhook',
                defaultMessage: 'Webhook action',
            })
        case 'externalTicket':
            return intl.formatMessage({
                id: 'component_task_details_policy_workflow_policy_step_provision_type_external_ticket',
                defaultMessage: 'External ticket',
            })
        case 'action':
            return intl.formatMessage({
                id: 'component_task_details_policy_workflow_policy_step_provision_type_actions',
                defaultMessage: 'Lifecycle action',
            })
        default:
            return intl.formatMessage({
                id: 'component_task_details_policy_workflow_policy_step_provision_type_not_found',
                defaultMessage: 'Provision type not found',
            })
    }
}

export const getManualProvisionFallbackErrori18n = ({
    stepProvision,
    isReassignedByError,
    task,
    intl,
}: {
    stepProvision?: Provision
    isReassignedByError: boolean
    task: TaskValue
    intl: IntlShape
}): string | null => {
    const stepIsConnectorOrDelegated =
        stepProvision?.provisionPolicy?.typ?.oneofKind === 'connector' ||
        stepProvision?.provisionPolicy?.typ?.oneofKind === 'delegated'

    if (!(stepIsConnectorOrDelegated && isReassignedByError)) {
        return null
    }

    switch (task.type?.taskType.oneofKind) {
        case 'grant':
            return intl.formatMessage({
                id: 'manual_prov_fallback_error',
                defaultMessage: 'Falling back to manual provision.',
            })
        case 'revoke':
            return intl.formatMessage({
                id: 'manual_deprov_fallback_error',
                defaultMessage: 'Falling back to manual deprovision.',
            })
        default:
            return null
    }
}

export const getUsersSelector = ({
    userIds,
    searchState,
}: {
    userIds: string[]
    searchState: UsersState
}): Array<UserValue> => {
    let users: Array<UserValue> = []
    if (searchState !== undefined) {
        const usersObj = searchState.get('list')
        if (usersObj.status === Status.OK) {
            users = usersObj.items
        }
    }

    return users.filter((user) => {
        return userIds.includes(user.id)
    })
}

export type StepWithStepInstance = {
    step: PolicyStep
    instance?: PolicyStepInstance
}

/**
 * Converts a WaitInstance to a Wait object with proper timestamp conversion
 * WaitInstance contains runtime state information, while Wait contains the policy definition
 */
export const convertWaitInstanceToWait = (waitInstance: WaitInstance): Wait => {
    const waitStep: Wait = {
        name: waitInstance.name,
        commentOnFirstWait: waitInstance.commentOnFirstWait,
        commentOnTimeout: waitInstance.commentOnTimeout,
        timeoutDuration: waitInstance.timeoutDuration,
        until: {
            oneofKind: 'condition',
            condition: {
                condition: '',
            },
        },
    }

    // Handle different until types
    if (waitInstance.until?.oneofKind === 'condition' && waitInstance.until.condition) {
        waitStep.until = {
            oneofKind: 'condition',
            condition: {
                condition: waitInstance.until.condition.condition || '',
            },
        }
    } else if (waitInstance.until?.oneofKind === 'untilTime' && waitInstance.until.untilTime?.untilTime) {
        // Convert Timestamp to hours/minutes/timezone
        const timestamp = waitInstance.until.untilTime.untilTime
        if (timestamp) {
            // Convert protobuf timestamp to Date
            const date = new Date(timestamp.seconds * 1000 + timestamp.nanos / 1000000)
            const hours = date.getUTCHours()
            const minutes = date.getUTCMinutes()

            waitStep.until = {
                oneofKind: 'untilTime',
                untilTime: {
                    hours,
                    minutes,
                    timezone: 'UTC', // Default to UTC, could be enhanced to detect timezone
                },
            }
        } else {
            // Fallback to default time if no timestamp
            waitStep.until = {
                oneofKind: 'untilTime',
                untilTime: {
                    hours: 9,
                    minutes: 0,
                    timezone: 'UTC',
                },
            }
        }
    } else {
        // Default to condition if no until type is specified
        waitStep.until = {
            oneofKind: 'condition',
            condition: {
                condition: '',
            },
        }
    }

    return waitStep
}

export const stepsFromInstance = (policyInstance?: PolicyInstance, task?: TaskValue): StepWithStepInstance[] => {
    if (!policyInstance) {
        return []
    }

    const steps: StepWithStepInstance[] = []
    for (const step of policyInstance.history) {
        if (task?.policyGenerationId !== '' && step.policyGenerationId !== task?.policyGenerationId) {
            continue
        }

        if (step.instance.oneofKind === 'approval' && step.instance.approval?.approval) {
            steps.push({
                step: PolicyStep.create({
                    step: {
                        oneofKind: 'approval',
                        approval: step.instance.approval.approval,
                    },
                }),
                instance: step,
            })
        }
        if (step.instance.oneofKind === 'provision' && step.instance.provision.provision) {
            steps.push({
                step: PolicyStep.create({
                    step: {
                        oneofKind: 'provision',
                        provision: step.instance.provision.provision,
                    },
                }),
                instance: step,
            })
        }
        if (step.instance.oneofKind === 'accept' && step.instance.accept) {
            steps.push({
                step: PolicyStep.create({
                    step: {
                        oneofKind: 'accept',
                        accept: step.instance.accept,
                    },
                }),
                instance: step,
            })
        }
        if (step.instance.oneofKind === 'reject' && step.instance.reject) {
            steps.push({
                step: PolicyStep.create({
                    step: {
                        oneofKind: 'reject',
                        reject: step.instance.reject,
                    },
                }),
                instance: step,
            })
        }
        if (step.instance.oneofKind === 'wait' && step.instance.wait) {
            steps.push({
                step: PolicyStep.create({
                    step: {
                        oneofKind: 'wait',
                        // eslint-disable-next-line testing-library/await-async-utils
                        wait: convertWaitInstanceToWait(step.instance.wait),
                    },
                }),
                instance: step,
            })
        }
        if (step.instance.oneofKind === 'form' && step.instance.form) {
            steps.push({
                step: PolicyStep.create({
                    step: {
                        oneofKind: 'form',
                        form: step.instance.form,
                    },
                }),
                instance: step,
            })
        }
        if (step.instance.oneofKind === 'action' && step.instance.action?.action) {
            steps.push({
                step: PolicyStep.create({
                    step: {
                        oneofKind: 'action',
                        action: step.instance.action.action,
                    },
                }),
                instance: step,
            })
        }
    }

    const current = policyInstance.current?.instance
    if (current?.oneofKind === 'approval' && current.approval.approval) {
        steps.push({
            step: PolicyStep.create({
                step: {
                    oneofKind: 'approval',
                    approval: current.approval.approval,
                },
            }),
            instance: policyInstance.current,
        })
    }
    if (current?.oneofKind === 'provision' && current.provision.provision) {
        steps.push({
            step: PolicyStep.create({
                step: {
                    oneofKind: 'provision',
                    provision: current.provision.provision,
                },
            }),
            instance: policyInstance.current,
        })
    }
    if (current?.oneofKind === 'accept' && current.accept) {
        steps.push({
            step: PolicyStep.create({
                step: {
                    oneofKind: 'accept',
                    accept: current.accept,
                },
            }),
            instance: policyInstance.current,
        })
    }
    if (current?.oneofKind === 'reject' && current.reject) {
        steps.push({
            step: PolicyStep.create({
                step: {
                    oneofKind: 'reject',
                    reject: current.reject,
                },
            }),
            instance: policyInstance.current,
        })
    }

    if (current?.oneofKind === 'wait' && current.wait) {
        steps.push({
            step: PolicyStep.create({
                step: {
                    oneofKind: 'wait',
                    // eslint-disable-next-line testing-library/await-async-utils
                    wait: convertWaitInstanceToWait(current.wait),
                },
            }),
            instance: policyInstance.current,
        })
    }

    if (current?.oneofKind === 'form' && current.form) {
        steps.push({
            step: PolicyStep.create({
                step: {
                    oneofKind: 'form',
                    form: current.form,
                },
            }),
            instance: policyInstance.current,
        })
    }

    if (current?.oneofKind === 'action' && current.action?.action) {
        steps.push({
            step: PolicyStep.create({
                step: {
                    oneofKind: 'action',
                    action: current.action.action,
                },
            }),
            instance: policyInstance.current,
        })
    }

    for (const step of policyInstance.next) {
        steps.push({
            step,
        })
    }

    return steps
}

export const currentStepInstance = ({
    steps,
    policy,
}: {
    steps?: StepWithStepInstance[]
    policy?: PolicyInstance
}): StepWithStepInstance | undefined => {
    if (!steps || !policy) {
        return undefined
    }
    const [first] = steps.filter((step) => policy.current?.id === step.instance?.id)

    return first
}
